import type { QuestionAnswerDistributionType } from 'types/api/diagnosisReportRefResponseType';

type QuestionOption = QuestionAnswerDistributionType['options'][number];

/**
 * 全設問の中で最大の選択肢数を返す。
 * 空配列の場合は 0 を返す。
 */
export function computeMaxOptionsCount(questions: QuestionAnswerDistributionType[]): number {
  return Math.max(0, ...questions.map(q => q.options.length));
}

/**
 * count / totalCount の比率から色クラスを返す。
 * 移行元 getColorClass() と完全一致の閾値を維持する。
 */
export function getColorClass(count: number, totalCount: number): string {
  if (totalCount === 0) return 'lowest';
  const percentage = (count / totalCount) * 100;
  if (percentage >= 35) return 'high';
  if (percentage >= 25) return 'medium';
  if (percentage >= 15) return 'low';
  return 'lowest';
}

/**
 * 設問ラベル文字列を返す。
 * questionTitle が存在する場合は「Q{questionNo} {questionTitle}」、
 * 無い場合は「Q{questionNo}」。
 */
export function getQuestionLabel(question: QuestionAnswerDistributionType): string {
  return question.questionTitle
    ? `Q${question.questionNo} ${question.questionTitle}`
    : `Q${question.questionNo}`;
}

/**
 * 選択肢を optionNo 昇順にソートして返す（非破壊コピー）。
 */
export function sortOptionsByNo(options: QuestionOption[]): QuestionOption[] {
  return [...options].sort((a, b) => a.optionNo - b.optionNo);
}

/**
 * 選択肢リストの count 合計を返す。
 * 空配列の場合は 0。
 */
export function computeOptionTotalCount(options: QuestionOption[]): number {
  return options.reduce((sum, o) => sum + o.count, 0);
}

/**
 * 選択肢の選択率ラベル文字列を返す。
 * totalCount > 0 の場合は toFixed(1) の文字列、0 の場合は '0'。
 * 移行元の percentage 計算と完全一致。
 */
export function getOptionPercentageLabel(count: number, totalCount: number): string {
  return totalCount > 0 ? ((count / totalCount) * 100).toFixed(1) : '0';
}

/**
 * 選択肢のタイトル文字列を返す。
 * optionTitle が null の場合は「選択肢{optionNo}」にフォールバック。
 */
export function getOptionTitle(option: QuestionOption): string {
  return option.optionTitle ?? `選択肢${option.optionNo}`;
}
